/**
 * Client-safe types + applicability filters for PUBLIC discount codes
 * (booking_addons rows with is_public = true), advertised on the /book
 * surfaces. Server loading lives in ./server/load-public-discount-codes.ts;
 * this module only narrows the already-loaded list per surface so location
 * or package switches never need a refetch.
 *
 * These filters are display-level heuristics only — redemption is always
 * re-validated server-side (validate_discount_code / redeem_discount_code),
 * so over-showing a code merely yields a friendly "invalid" on apply.
 */

export interface PublicDiscountCode {
  id: string;
  code: string;
  name: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  /** NULL = both booking surfaces; 'slot' | 'package' | 'product' | 'membership'. */
  applies_to: 'slot' | 'package' | 'product' | 'membership' | null;
  /** NULL = any activity category. */
  scope_package_type: string | null;
  scope_package_types?: string[] | null;
  /** Legacy singleton, used when scope_package_ids is absent or NULL. */
  scope_package_id: string | null;
  /** Empty = any package; nonempty = only these booking_packages rows. */
  scope_package_ids?: string[] | null;
  /** Empty = any membership plan; nonempty = only these plans. */
  scope_membership_plan_ids?: string[] | null;
  /** NULL = any template (slot surface narrowing). */
  scope_template_id: string | null;
  scope_template_ids?: string[] | null;
  scope_class_type_ids?: string[] | null;
  applicable_days_of_week: number[] | null;
  applicable_hours_start: string | null;
  applicable_hours_end: string | null;
  /** Empty = redeemable at all locations. */
  location_ids: string[];
  valid_until: string | null;
  booking_date_from?: string | null;
  booking_date_until?: string | null;
}

/** Codes redeemable when purchasing this specific package. */
export function publicCodesForPackage(
  codes: PublicDiscountCode[],
  pkg: {
    id: string;
    package_type?: string | null;
    location_id?: string | null;
  },
): PublicDiscountCode[] {
  return codes.filter((c) => {
    const templateIds =
      c.scope_template_ids ?? (c.scope_template_id ? [c.scope_template_id] : []);
    const packageTypes =
      c.scope_package_types ?? (c.scope_package_type ? [c.scope_package_type] : []);
    if (c.applies_to && c.applies_to !== 'package') return false;
    if (
      templateIds.length > 0 ||
      (c.scope_class_type_ids?.length ?? 0) > 0 ||
      c.booking_date_from != null ||
      c.booking_date_until != null ||
      (c.applicable_days_of_week?.length ?? 0) > 0 ||
      c.applicable_hours_start !== null ||
      c.applicable_hours_end !== null
    )
      return false;
    const packageIds =
      c.scope_package_ids ?? (c.scope_package_id ? [c.scope_package_id] : []);
    if (packageIds.length > 0 && !packageIds.includes(pkg.id)) return false;
    if (
      packageTypes.length > 0 &&
      pkg.package_type &&
      !packageTypes.includes(pkg.package_type)
    )
      return false;
    if (
      c.location_ids.length > 0 &&
      (!pkg.location_id || !c.location_ids.includes(pkg.location_id))
    )
      return false;
    return true;
  });
}

/** Codes redeemable on the single-booking (slot / class) surface. */
export function publicCodesForSlotSurface(
  codes: PublicDiscountCode[],
  ctx: {
    bookingType?: string | null;
    templateId?: string | null;
    classTypeId?: string | null;
    locationId?: string | null;
    bookingDate?: string | null;
  } = {},
): PublicDiscountCode[] {
  return codes.filter((c) => {
    const templateIds =
      c.scope_template_ids ?? (c.scope_template_id ? [c.scope_template_id] : []);
    const packageTypes =
      c.scope_package_types ?? (c.scope_package_type ? [c.scope_package_type] : []);
    if (c.applies_to && c.applies_to !== 'slot') return false;
    if (c.scope_class_type_ids?.length) {
      if (!ctx.classTypeId || !c.scope_class_type_ids.includes(ctx.classTypeId)) return false;
      if (packageTypes.length > 0 && !ctx.bookingType) return false;
      if (templateIds.length > 0 && !ctx.templateId) return false;
    }
    if (c.booking_date_from || c.booking_date_until) {
      if (!ctx.bookingDate) return false;
      if (c.booking_date_from && ctx.bookingDate < c.booking_date_from) return false;
      if (c.booking_date_until && ctx.bookingDate > c.booking_date_until) return false;
    }
    const packageIds =
      c.scope_package_ids ?? (c.scope_package_id ? [c.scope_package_id] : []);
    if (packageIds.length > 0) return false;
    if (
      packageTypes.length > 0 &&
      ctx.bookingType &&
      !packageTypes.includes(ctx.bookingType)
    )
      return false;
    if (
      templateIds.length > 0 &&
      ctx.templateId &&
      !templateIds.includes(ctx.templateId)
    )
      return false;
    if (
      c.location_ids.length > 0 &&
      ctx.locationId &&
      !c.location_ids.includes(ctx.locationId)
    )
      return false;
    return true;
  });
}

/** Codes redeemable when buying this membership plan. */
export function publicCodesForMembership(
  codes: PublicDiscountCode[],
  plan: { id: string; location_id?: string | null },
): PublicDiscountCode[] {
  return codes.filter((c) => {
    if (c.applies_to !== 'membership') return false;
    const planIds = c.scope_membership_plan_ids ?? [];
    if (planIds.length > 0 && !planIds.includes(plan.id)) return false;
    // A location-scoped code needs the plan's own location; a plan sold at
    // every location has none, so the server would refuse it.
    if (
      c.location_ids.length > 0 &&
      (!plan.location_id || !c.location_ids.includes(plan.location_id))
    )
      return false;
    return true;
  });
}
